import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, auth } from '../firebase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { FaArrowLeft, FaCheck, FaTimes, FaExclamationTriangle, FaDownload, FaArrowRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const MCHAT_QUESTIONS = [
  { id: 1, text: "Does your child enjoy being swung, bounced on your knee, etc.?", risk_answer: "no", critical: false },
  { id: 2, text: "Does your child take an interest in other children?", risk_answer: "no", critical: true },
  { id: 3, text: "Does your child like climbing on things, such as stairs?", risk_answer: "no", critical: false },
  { id: 4, text: "Does your child enjoy playing peek-a-boo/hide-and-seek?", risk_answer: "no", critical: false },
  { id: 5, text: "Does your child ever pretend, for example, to talk on the phone or take care of a doll or anything else?", risk_answer: "no", critical: true },
  { id: 6, text: "Does your child ever use his/her index finger to point, to ask for something?", risk_answer: "no", critical: false },
  { id: 7, text: "Does your child ever use his/her index finger to point, to indicate interest in something?", risk_answer: "no", critical: true },
  { id: 8, text: "Can your child play properly with small toys (e.g. cars or bricks) without just mouthing, fiddling, or dropping them?", risk_answer: "no", critical: false },
  { id: 9, text: "Does your child ever bring objects over to you (parent) to show you something?", risk_answer: "no", critical: true },
  { id: 10, text: "Does your child look you in the eye for more than a second or two?", risk_answer: "no", critical: false },
  { id: 11, text: "Does your child ever seem oversensitive to noise? (e.g., plugging ears)", risk_answer: "yes", critical: false },
  { id: 12, text: "Does your child smile in response to your face or your smile?", risk_answer: "no", critical: false },
  { id: 13, text: "Does your child imitate you? (e.g., you make a face-will your child imitate it?)", risk_answer: "no", critical: true },
  { id: 14, text: "Does your child respond to his/her name when you call?", risk_answer: "no", critical: true },
  { id: 15, text: "If you point at a toy across the room, does your child look at it?", risk_answer: "no", critical: true },
  { id: 16, text: "Does your child walk?", risk_answer: "no", critical: false },
  { id: 17, text: "Does your child look at things you are looking at?", risk_answer: "no", critical: false },
  { id: 18, text: "Does your child make unusual finger movements near his/her face?", risk_answer: "yes", critical: false },
  { id: 19, text: "Does your child try to attract your attention to his/her activity?", risk_answer: "no", critical: false },
  { id: 20, text: "Have you ever wondered if your child is deaf?", risk_answer: "yes", critical: false },
];

const MChatAssessment = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [riskLevel, setRiskLevel] = useState('');
  const [riskScore, setRiskScore] = useState(0);
  const [user, setUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assessmentId, setAssessmentId] = useState(null);
  const [hasExistingReport, setHasExistingReport] = useState(false);
  const [pendingAssessmentData, setPendingAssessmentData] = useState(null);
  
  // Initialize hooks
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get child details from location state
  const { childName, childsAgeInMonths, childGender, mobileNumber } = location.state || {};
  
  // Check if user is logged in
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      if (user) {
        // Check if user has existing assessment
        checkExistingReport(user.uid);
        // If there's pending assessment data, save it now
        if (pendingAssessmentData) {
          saveAssessment(pendingAssessmentData);
        }
      }
    });
    
    // Log the received state for debugging
    console.log('Location state:', location.state);
    
    return () => unsubscribe();
  }, []);
  
  // Check if user already has a report
  const checkExistingReport = async (userId) => {
    try {
      const q = query(
        collection(db, 'assessments'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      const hasReport = !querySnapshot.empty;
      
      if (hasReport) {
        const reportData = querySnapshot.docs[0].data();
        // If we're already on the results page, just update the UI
        if (showResults) {
          setHasExistingReport(true);
          setAssessmentId(querySnapshot.docs[0].id);
        }
      }
      
      return hasReport;
    } catch (error) {
      console.error('Error checking for existing report:', error);
      return false;
    }
  };

  // Redirect if child details are missing
  useEffect(() => {
    if (!childName || !childsAgeInMonths) {
      console.warn('MChatAssessment: Child details not found in location state. Redirecting to free-assessment.');
      navigate('/free-assessment', { replace: true });
    }
  }, [childName, childsAgeInMonths, navigate]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      if (currentUser && pendingAssessmentData) {
        try {
          // Check if user already has an assessment
          const hasExisting = await checkExistingReport(currentUser.uid);
          if (hasExisting) {
            setHasExistingReport(true);
            return;
          }
          
          // Save the assessment data
          const docRef = await addDoc(collection(db, 'assessments'), {
            ...pendingAssessmentData,
            userId: currentUser.uid,
            parentEmail: currentUser.email || pendingAssessmentData.parentEmail,
            parentName: currentUser.displayName || pendingAssessmentData.parentName || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            status: 'completed'
          });
          
          // Clear the pending data
          setPendingAssessmentData(null);
          localStorage.removeItem('pendingAssessment');
          
          // Set the assessment ID for the download
          setAssessmentId(docRef.id);
          
          // Show success message
          alert('Your assessment has been saved! You can now download your report.');
          
        } catch (error) {
          console.error('Error saving assessment after signup:', error);
          alert('There was an error saving your assessment. Please try again.');
        } finally {
          setIsSubmitting(false);
        }
      }
    });
    return () => unsubscribe();
  }, [pendingAssessmentData]);

  const handleAnswer = (answer) => {
    const updatedAnswers = {
      ...answers,
      [MCHAT_QUESTIONS[currentQuestionIndex].id]: answer,
    };
    setAnswers(updatedAnswers);

    if (currentQuestionIndex < MCHAT_QUESTIONS.length - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
    } else {
      calculateScoreAndFinalize(updatedAnswers);
    }
  };

  const calculateScoreAndFinalize = async (currentAnswers) => {
    setIsSubmitting(true);

    let score = 0;
    let criticalFails = 0;
    const failedItemsList = [];
    const criticalItemsFailedList = [];

    // Calculate score and critical fails
    MCHAT_QUESTIONS.forEach(q => {
      if (currentAnswers[q.id] === q.risk_answer) {
        score++;
        failedItemsList.push(q.id);
        if (q.critical) {
          criticalFails++;
          criticalItemsFailedList.push(q.id);
        }
      }
    });

    // Determine risk level
    let determinedRiskLevel = 'Low Risk';
    if (score >= 8) {
      determinedRiskLevel = 'High Risk';
    } else if (score >= 3 || criticalFails >= 2) {
      determinedRiskLevel = 'Medium Risk';
    }
    
    setRiskScore(score);
    setRiskLevel(determinedRiskLevel);
    
    // Prepare assessment data
    const assessmentDataPayload = {
      childName: childName.trim(),
      childsAgeInMonths: parseInt(childsAgeInMonths, 10),
      childGender: childGender,
      ...(mobileNumber && { parentMobile: mobileNumber }), // Include mobile number if provided
      answers: currentAnswers,
      score,
      riskLevel: determinedRiskLevel,
      failedItems: failedItemsList,
      criticalItemsFailed: criticalItemsFailedList,
      criticalFails,
      assessmentType: "mchat",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'completed'
    };
    
    setPendingAssessmentData(assessmentDataPayload);
    
    // Set the results to show the summary page
    setShowResults(true);
    
    // If user is not logged in, we'll show the summary page first
    if (!user) {
      // Store the assessment data in localStorage temporarily
      const tempId = `temp_${Date.now()}`;
      localStorage.setItem('pendingAssessment', JSON.stringify({
        ...assessmentDataPayload,
        tempId
      }));
      return;
    }
    
    // For logged-in users, save the assessment
    await saveAssessment(assessmentDataPayload);
    
    setShowResults(true);
    setIsSubmitting(false);
  };
  
  const saveAssessment = async (assessmentData) => {
    try {
      // Save to database
      const docRef = await addDoc(collection(db, 'assessments'), {
        ...assessmentData,
        userId: user.uid,
        userRef: doc(db, 'users', user.uid)
      });
      
      const newAssessmentId = docRef.id;
      console.log('Assessment saved with ID:', newAssessmentId);
      
      // Update user's document with assessment reference
      try {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, {
          lastAssessment: newAssessmentId,
          lastAssessmentDate: serverTimestamp(),
          updatedAt: serverTimestamp(),
          assessmentCount: ((await getDoc(userDocRef)).data()?.assessmentCount || 0) + 1
        }, { merge: true });
        console.log('User document updated with assessment reference');
      } catch (userUpdateError) {
        console.warn('Could not update user document:', userUpdateError);
      }
      
      // Navigate to report page
      window.location.href = `/app/report/${newAssessmentId}`;
      
    } catch (error) {
      console.error('Error saving assessment:', error);
      alert('Failed to save assessment. Please try again.');
    }
  };

  const handleSaveAndRedirect = async (assessmentData, currentUser) => {
    if (!currentUser || !assessmentData) {
      console.error("User or assessment data missing for save.");
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Create assessment in the top-level 'assessments' collection
      const assessmentsCollectionRef = collection(db, 'assessments');
      const newAssessmentRef = await addDoc(assessmentsCollectionRef, {
        ...assessmentData,
        userId: currentUser.uid,
        userRef: doc(db, 'users', currentUser.uid)
      });
      
      console.log("Assessment saved with ID: ", newAssessmentRef.id);
      
      // Also update the user's document to include a reference to this assessment
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        await setDoc(userDocRef, {
          lastAssessment: newAssessmentRef.id,
          lastAssessmentDate: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // Initialize assessments array if it doesn't exist
          assessmentCount: (await getDoc(userDocRef)).data()?.assessmentCount + 1 || 1
        }, { merge: true });
      } catch (userUpdateError) {
        console.warn("Could not update user document with assessment reference:", userUpdateError);
        // Continue even if we can't update the user document
      }
      
      // Store assessment ID in session storage for immediate access
      sessionStorage.setItem('lastAssessmentId', newAssessmentRef.id);
      
      // Navigate to the report page
      navigate(`/app/report/${newAssessmentRef.id}`);

    } catch (error) {
      console.error("Error saving assessment to Firestore: ", error);
      
      // If we don't have a user, try to save the assessment locally
      if (error.code === 'permission-denied' || !currentUser.uid) {
        try {
          const pendingAssessments = JSON.parse(localStorage.getItem('pendingAssessments') || '[]');
          const tempId = `temp_${Date.now()}`;
          pendingAssessments.push({
            ...assessmentData,
            tempId,
            createdAt: new Date().toISOString()
          });
          localStorage.setItem('pendingAssessments', JSON.stringify(pendingAssessments));
          console.log("Assessment saved locally. Will sync when signed in.");
          // Redirect to signup with the temp ID
          navigate(`/auth?redirect=/app/report/${tempId}`);
          return;
        } catch (localError) {
          console.error("Error saving assessment locally:", localError);
        }
      }
      
      alert("There was an error saving your assessment. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleSignIn = async () => {
    if (!pendingAssessmentData) {
        alert("No assessment data to save. Please complete an assessment first.");
        return;
    }
    setIsSubmitting(true);  
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      // onAuthStateChanged in useEffect will handle saving pendingAssessmentData
      // and navigation if sign-in is successful.
    } catch (error) {
      console.error("Error during Google sign-in: ", error);
      alert(`Sign-in failed: ${error.message}. Please try again.`);
      setIsSubmitting(false); 
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prevIndex => prevIndex - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
    setRiskLevel('');
    setRiskScore(0);
    setPendingAssessmentData(null);
    setIsSubmitting(false);
  };

  const handleDownloadReport = () => {
    console.log('handleDownloadReport called');
    console.log('User:', user);
    console.log('Assessment ID:', assessmentId);
    
    if (!user) {
      console.log('User not logged in, redirecting to signup');
      // Redirect to signup with return URL
      navigate('/auth', { 
        state: { 
          redirectTo: `/app/report/${assessmentId}`,
          message: 'Sign up to download your assessment report'
        } 
      });
      return;
    }
    
    // If user is logged in, proceed to download
    if (assessmentId) {
      console.log('Navigating to report with ID:', assessmentId);
      // Use window.location to ensure a full page reload
      window.location.href = `/app/report/${assessmentId}`;
    } else {
      console.error('No assessmentId available for report generation');
      alert('Error: No assessment data available. Please complete the assessment first.');
    }
  };


  const renderLoadingState = () => (
    <div className="text-center">
      <p className="text-sky-600 text-xl">Checking your account status...</p>
    </div>
  );

  const handleGetReport = async (e) => {
    // Prevent default form submission if any
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('handleGetReport called', { user, pendingAssessmentData });
    
    if (!user) {
      // For non-logged-in users, trigger the signup flow
      const tempId = `temp_${Date.now()}`;
      const assessmentToSave = {
        ...pendingAssessmentData,
        tempId,
        createdAt: new Date().toISOString()
      };
      
      console.log('Saving temporary assessment:', assessmentToSave);
      localStorage.setItem('pendingAssessment', JSON.stringify(assessmentToSave));
      
      // Try to trigger the header's signup modal
      if (typeof window.showSignupModal === 'function') {
        console.log('Calling window.showSignupModal');
        window.showSignupModal(`/app/report/${tempId}`);
        return;
      } 
      
      if (window.parent && typeof window.parent.showSignupModal === 'function') {
        // Try parent window if in an iframe
        console.log('Calling parent window.showSignupModal');
        window.parent.showSignupModal(`/app/report/${tempId}`);
        return;
      }
      
      console.log('showSignupModal not found, falling back to auth page');
      // Fallback to redirect if the modal function isn't available
      window.location.href = `/auth?redirect=/app/report/${tempId}&source=assessment`;
      return;
    } else {
      // For logged-in users, proceed to save and show the report
      try {
        setIsSubmitting(true);
        const docRef = await addDoc(collection(db, 'assessments'), {
          ...pendingAssessmentData,
          userId: user.uid,
          userRef: doc(db, 'users', user.uid),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: 'completed'
        });
        
        console.log('Assessment saved with ID:', docRef.id);
        window.location.href = `/app/report/${docRef.id}`;
      } catch (error) {
        console.error('Error saving assessment:', error);
        alert('Failed to save assessment. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  const renderResultsView = () => {
    return (
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-lg p-6 md:p-8">
        {/* Progress Steps */}
        <div className="flex justify-between items-center mb-8 relative">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-green-100 text-green-600">
              1
            </div>
            <span className="text-xs font-medium text-gray-700">Complete Assessment</span>
          </div>
          
          <div className="h-1 w-1/4 bg-green-100 mx-2">
            <div className="h-full bg-green-500"></div>
          </div>
          
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-green-100 text-green-600">
              2
            </div>
            <span className="text-xs font-medium text-gray-700">View Results</span>
          </div>
          
          <div className="h-1 w-1/4 bg-gray-200 mx-2">
            <div className="h-full bg-green-500"></div>
          </div>
          
          <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${user ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
              3
            </div>
            <span className={`text-xs font-medium ${user ? 'text-gray-700' : 'text-gray-400'}`}>
              {user ? 'View Report' : 'Sign Up for Report'}
            </span>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold mb-6">Assessment Complete</h2>
          <p className="text-lg text-gray-700 mb-8">
            Thank you for completing the M-CHAT assessment for {childName}.
          </p>
          
          <div className="bg-blue-50 p-6 rounded-lg mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h4 className="text-lg font-semibold text-gray-900">M-CHAT-R/F Assessment Report</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Includes detailed analysis and recommendations
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 line-through">₹999</p>
                <p className="text-2xl font-bold text-green-600">FREE</p>
              </div>
            </div>
          </div>

          {user ? (
            // Logged in user - show View Report button
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={handleGetReport}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg shadow-md transform hover:scale-105 transition-all duration-200 w-full max-w-xs"
              >
                View Full Report
                <FaArrowRight className="ml-1" />
              </button>
              <p className="text-sm text-gray-600">
                Your assessment has been saved to your account.
              </p>
            </div>
          ) : (
            // Not logged in - show Sign Up CTA
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={(e) => {
                  console.log('Button clicked, calling handleGetReport');
                  handleGetReport(e);
                }}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-3 px-6 rounded-lg shadow-md transform hover:scale-105 transition-all duration-200 w-full max-w-xs"
                type="button"
              >
                Get Your Report Now
                <FaArrowRight className="ml-1" />
              </button>
              <p className="text-sm text-gray-600">
                Sign up to view and save your assessment report.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (showResults) {
      return renderResultsView();
    }

    const currentQuestion = MCHAT_QUESTIONS[currentQuestionIndex];
    
    return (
      <div className="w-full max-w-4xl">
        <div className="bg-white p-6 md:p-8 rounded-lg shadow-xl w-full">
          <div className="text-right text-gray-500 mb-2 font-medium">
            Question {currentQuestionIndex + 1} of {MCHAT_QUESTIONS.length} (For: {childName}, {childsAgeInMonths} months{childGender ? `, ${childGender}` : ''})
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
            <div 
              className="bg-sky-500 h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${((currentQuestionIndex + 1) / MCHAT_QUESTIONS.length) * 100}%` }}
            />
          </div>
          <h2 className="text-xl md:text-2xl font-semibold mb-8 text-center text-gray-800">
            {currentQuestion.text}
          </h2>
          <div className="flex flex-col sm:flex-row justify-around space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => handleAnswer('yes')}
              className="w-full sm:w-auto flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-lg text-lg md:text-xl shadow-md transform hover:scale-105 transition-transform duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-opacity-75"
            >
              👍 Yes
            </button>
            <button
              onClick={() => handleAnswer('no')}
              className="w-full sm:w-auto flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-4 px-6 rounded-lg text-lg md:text-xl shadow-md transform hover:scale-105 transition-transform duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75"
            >
              👎 No
            </button>
          </div>
          {currentQuestionIndex > 0 && (
            <div className="mt-8 text-center">
              <button 
                onClick={handlePrevious}
                className="text-sm text-sky-600 hover:text-sky-800 hover:underline"
              >
                ← Previous Question
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {showResults ? (
          renderResultsView()
        ) : (
          <div className="w-full">
            <div className="bg-white p-6 md:p-8 rounded-lg shadow-xl w-full">
              <div className="text-right text-gray-500 mb-2 font-medium">
                Question {currentQuestionIndex + 1} of {MCHAT_QUESTIONS.length} (For: {childName}, {childsAgeInMonths} months{childGender ? `, ${childGender}` : ''})
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
                <div 
                  className="bg-sky-500 h-2.5 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${((currentQuestionIndex + 1) / MCHAT_QUESTIONS.length) * 100}%` }}
                />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold mb-8 text-center text-gray-800">
                {MCHAT_QUESTIONS[currentQuestionIndex].text}
              </h2>
              <div className="flex flex-col sm:flex-row justify-around space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => handleAnswer('yes')}
                  className="w-full sm:w-auto flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-lg text-lg md:text-xl shadow-md transform hover:scale-105 transition-transform duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-opacity-75"
                >
                  👍 Yes
                </button>
                <button
                  onClick={() => handleAnswer('no')}
                  className="w-full sm:w-auto flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-4 px-6 rounded-lg text-lg md:text-xl shadow-md transform hover:scale-105 transition-transform duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75"
                >
                  👎 No
                </button>
              </div>
              {currentQuestionIndex > 0 && (
                <div className="mt-8 text-center">
                  <button 
                    onClick={handlePrevious}
                    className="text-sm text-sky-600 hover:text-sky-800 hover:underline"
                  >
                    ← Previous Question
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      

      
      <div id="recaptcha-container"></div>
      <Footer />
    </div>
  );
}

export default MChatAssessment;
